import React from 'react';
import { Reveal } from './motion';
import WorkoutMockup from './mockups/WorkoutMockup';
import ResultsMockup from './mockups/ResultsMockup';
import EquipmentMockup from './mockups/EquipmentMockup';
import AnalyticsMockup from './mockups/AnalyticsMockup';

const FEATURES = [
  {
    eyebrow: 'Daily operations',
    title: 'The whole session, decided before anyone reaches the dock.',
    body: 'Build tomorrow\u2019s workout with boats, blades and seats assigned. Athletes open the app and see their crew, the piece, and exactly what the river is doing \u2014 air and water temperature, wind, and flow pulled live from your USGS gauge.',
    bullets: [
      'Saved lineup templates across your regular boatings',
      'Squad-scoped sessions, sign-up caps and coach-added walk-ons',
      'Prognostic target splits printed on the piece when you set a goal percentage',
    ],
    Mock: WorkoutMockup,
  },
  {
    eyebrow: 'Testing & benchmarking',
    title: 'A 2k is just a number until you know what it\u2019s worth.',
    body: 'Publish a structured test piece, athletes submit from the app, and every result comes back scored against the two things that actually matter: the athlete\u2019s goal 2k and the Gold Medal Standard for their boat class.',
    bullets: [
      'Coach-built templates with intervals, rates and logged conditions',
      'Automatic % of goal 2k and % GMS on every single result',
      'Shareable reports you can send to the squad or keep internal',
    ],
    Mock: ResultsMockup,
    flip: true,
  },
  {
    eyebrow: 'The boathouse',
    title: 'Every hull, blade and erg \u2014 tracked down to the seat.',
    body: 'Reservations that stop two crews claiming the same eight. Meters attributed to the nine people who were actually in the boat. Rigging notes, private versus club ownership, and damage reports that don\u2019t die in a group chat.',
    bullets: [
      'Oar sets you can split into pairs and recombine',
      'Damage tickets with severity, status and history',
      'Meter reports per boat, per athlete, per season',
    ],
    Mock: EquipmentMockup,
  },
  {
    eyebrow: 'Coach analytics',
    title: 'Answer the only question that matters: who is getting faster?',
    body: 'Progression over time, athlete-against-athlete comparison, improvement relative to the squad, training volume by zone and compliance against the plan you actually wrote. Export any of it to CSV when you need it elsewhere.',
    bullets: [
      'Custom result groups for the comparisons you run every season',
      'Plan compliance that shows who is doing the prescribed work',
      'Per-athlete drill-down straight from the team view',
    ],
    Mock: AnalyticsMockup,
    flip: true,
  },
];

const EXTRAS = [
  ['Attendance & absences', 'Athletes flag absences ahead of time; coaches get a calendar and ranked attendance over any lookback window.'],
  ['Training plans', 'Multi-week plans with custom zones that prefill the athlete\u2019s training log and feed compliance reporting.'],
  ['Squads', 'Slice a team into port, starboard, lightweight or novice and scope workouts and reports to the squad.'],
  ['Announcements', 'Email the whole club or a single team, with per-member opt-out respected.'],
  ['Invite-only access', 'No open signup. Every account starts from an invitation with a role and team already attached.'],
  ['CSV import', 'Bulk-load rosters, workouts and training plans instead of typing a season in by hand.'],
  ['Multi-club & multi-team', 'One login can belong to several clubs with a different role in each, and switch between them.'],
  ['Club branding', 'Your name, colors and logo across the app, the browser tab and the favicon.'],
];

function FeatureRow({ feature, index }) {
  const { eyebrow, title, body, bullets, Mock, flip } = feature;
  return (
    <div
      className={`grid items-center gap-12 py-16 sm:py-20 lg:grid-cols-2 lg:gap-16 ${
        index > 0 ? 'border-t border-slate-200/70' : ''
      }`}
    >
      <Reveal className={flip ? 'lg:order-2' : ''}>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">{eyebrow}</p>
        <h3 className="mt-3 text-2xl font-bold leading-tight tracking-tight text-slate-900 sm:text-3xl">
          {title}
        </h3>
        <p className="mt-4 text-base leading-relaxed text-slate-600">{body}</p>
        <ul className="mt-6 space-y-3">
          {bullets.map((b) => (
            <li key={b} className="flex gap-3 text-sm text-slate-700">
              <span
                className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600"
                aria-hidden="true"
              />
              <span className="leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.12} y={36} className={flip ? 'lg:order-1' : ''}>
        <Mock />
      </Reveal>
    </div>
  );
}

export default function FeatureSections() {
  return (
    <section id="features" className="bg-white">
      <div className="mx-auto max-w-6xl safe-area-x sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl pt-20 sm:pt-28">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
            What you actually get
          </p>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Four things every program fights with. Solved properly.
          </h2>
        </Reveal>

        {FEATURES.map((f, i) => (
          <FeatureRow key={f.title} feature={f} index={i} />
        ))}
      </div>

      <div className="border-t border-slate-200/70 bg-slate-50">
        <div className="mx-auto max-w-6xl safe-area-x py-16 sm:px-6 sm:py-20 lg:px-8">
          <Reveal>
            <h3 className="text-2xl font-bold tracking-tight text-slate-900">
              And everything else a season needs
            </h3>
          </Reveal>
          <div className="mt-8 grid gap-x-8 gap-y-7 sm:grid-cols-2 lg:grid-cols-4">
            {EXTRAS.map(([title, body], i) => (
              <Reveal key={title} delay={(i % 4) * 0.06} y={20}>
                <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
